import React from "react";
import { useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";
import Faq from "./Faq";
import freeListingHero from "../image/listings/free-listing-hero-v1.webp";
import listingManagementImage from "../image/listings/listing-showcase-management-v1.webp";
import listingDiscoveryImage from "../image/listings/listing-showcase-discovery-v1.webp";
import listingBenefitsDiscoveryImage from "../image/listings/listing-benefits-discovery-v1.webp";
import listingBenefitsTrustImage from "../image/listings/listing-benefits-trust-v1.webp";
import listingBenefitsOperationsImage from "../image/listings/listing-benefits-operations-v1.webp";
import playstore from "../image/meragym-google-play-badge.png";
import appstore from "../image/meragym-app-store-badge.png";
import { Helmet, HelmetProvider } from "react-helmet-async";

export default function GymListing() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gymName, setGymName] = useState("");

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    city: "",
    message: "",
    owner_name: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(
        `${config.apiUrl}/business-list-request`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );
      setGymName(formData.name);
      setFormData({ name: "", address: "", email: "", phone: "", city: "", message: "", owner_name: "" });
      setErrors({});
      setSubmitted(true);
    } catch (error) {
      if (error.response?.status === 422) {
        const e = error.response.data.errors || {};
        setErrors({
          name:       e.name?.[0]       || "",
          owner_name: e.owner_name?.[0] || "",
          email:      e.email?.[0]      || "",
          phone:      e.phone?.[0]      || "",
          city:       e.city?.[0]       || "",
          address:    e.address?.[0]    || "",
        });
        toast.error(error.response.data.message || "Please fix the errors below.", { position: "top-right", autoClose: 3000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Free Gym Listing — MeraGym",
    description: "List your gym or fitness center for free on MeraGym. Reach thousands of fitness seekers, get leads, and grow your gym business online at zero cost.",
    provider: { "@type": "Organization", name: "MeraGym", url: "https://meragym.com" },
    serviceType: "Gym Directory Listing",
    areaServed: { "@type": "Country", name: "India" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "INR", description: "Free gym listing forever" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://meragym.com" },
      { "@type": "ListItem", position: 2, name: "Free Gym Listing", item: "https://meragym.com/free-gym-listing" },
    ],
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Free Gym Listing India — Register Your Gym Online | MeraGym</title>
          <meta name="description" content="List your gym or fitness center on MeraGym for free. Reach thousands of fitness seekers in your city, get real leads and enquiries online. 100% free — no credit card needed." />
          <meta name="keywords" content="free gym listing India, register gym online, gym directory India, list gym for free, gym listing website India, add gym online, gym owner registration, fitness center listing" />
          <link rel="canonical" href="https://meragym.com/free-gym-listing" />
          <meta property="og:title" content="Free Gym Listing India — Register Your Gym Online | MeraGym" />
          <meta property="og:description" content="List your gym for free on MeraGym and start getting leads from fitness seekers near you. 5,000+ gyms already listed." />
          <meta property="og:url" content="https://meragym.com/free-gym-listing" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Free Gym Listing India | MeraGym" />
          <meta name="twitter:description" content="Register your gym for free and get leads online. Join 5,000+ gyms on MeraGym." />
          <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      </HelmetProvider>

      <ToastContainer />

      {/* ── Hero / Registration Form ── */}
      <div
        className="gym-listing-section"
        style={{ "--free-listing-hero": `url(${freeListingHero})` }}
      >
        <div className="bg-overlay"></div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 col-md-12 position-relative my-auto">
              <div className="listing-intro">
                <span className="listing-intro-badge">100% Free · No Credit Card</span>
                <h1>List Your Gym &amp; Fitness Center Online for Free</h1>
                <p>
                  List your <b className="text-danger">Gym</b> or Fitness Center for free and start getting leads &amp; enquiries online today.
                </p>
                <div className="listing-intro-stats">
                  <div className="listing-intro-stat">
                    <strong>5,000+</strong>
                    <span>Gyms Listed</span>
                  </div>
                  <div className="listing-intro-stat">
                    <strong>50+</strong>
                    <span>Cities</span>
                  </div>
                  <div className="listing-intro-stat">
                    <strong>24h</strong>
                    <span>Review Time</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7 col-md-12">
              <div className="listing-form-card">

                {submitted ? (
                  <div className="listing-success">
                    <div className="listing-success-icon">
                      <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <h2>Your Gym is Registered!</h2>
                    <p>
                      <strong>{gymName}</strong> has been successfully submitted. Our team will review and publish your listing within <strong>24 hours</strong>.
                    </p>
                    <div className="listing-success-steps">
                      <div className="listing-success-step">
                        <i className="bi bi-check2"></i>
                        <span>Details saved</span>
                      </div>
                      <div className="listing-success-step">
                        <i className="bi bi-hourglass-split"></i>
                        <span>Under review</span>
                      </div>
                      <div className="listing-success-step">
                        <i className="bi bi-rocket-takeoff"></i>
                        <span>Goes live in 24h</span>
                      </div>
                    </div>
                    <button
                      className="listing-success-btn"
                      onClick={() => { setSubmitted(false); setGymName(""); }}
                    >
                      Register Another Gym
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="listing-form-progress" aria-label="Your listing journey">
                      <div className="listing-form-progress-item is-active">
                        <span>1</span><small>Gym details</small>
                      </div>
                      <div className="listing-form-progress-line"></div>
                      <div className="listing-form-progress-item">
                        <span>2</span><small>Team review</small>
                      </div>
                      <div className="listing-form-progress-line"></div>
                      <div className="listing-form-progress-item">
                        <span>3</span><small>Go live</small>
                      </div>
                    </div>
                    <div className="listing-form-heading">
                      <h2>Register Your Gym — Free</h2>
                      <p>Start with your gym details. We review most listings within 24 hours.</p>
                    </div>

                    <form className="usr-form" onSubmit={handleSubmit} noValidate>
                      <div className="row contact-user-row">
                        <div className="col-sm-8 col-md-8">
                          <label className="listing-form-label">Gym Name <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="e.g. FitZone Gym" name="name" value={formData.name} onChange={handleChange} />
                          {errors.name && <small className="text-danger">{errors.name}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">Owner Name <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="Your name" name="owner_name" value={formData.owner_name} onChange={handleChange} />
                          {errors.owner_name && <small className="text-danger">{errors.owner_name}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-4 col-md-4">
                          <label className="listing-form-label">Email Address <span className="text-danger">*</span></label>
                          <input type="email" className="form-control" placeholder="you@example.com" name="email" value={formData.email} onChange={handleChange} />
                          {errors.email && <small className="text-danger">{errors.email}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">Phone Number <span className="text-danger">*</span></label>
                          <input type="tel" className="form-control" placeholder="9XXXXXXXXX" name="phone" value={formData.phone} onChange={handleChange} />
                          {errors.phone && <small className="text-danger">{errors.phone}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">City <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="Varanasi" name="city" value={formData.city} onChange={handleChange} />
                          {errors.city && <small className="text-danger">{errors.city}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-12">
                          <label className="listing-form-label">Full Gym Address <span className="text-danger">*</span></label>
                          <textarea className="form-control" rows="2" name="address" value={formData.address} onChange={handleChange} placeholder="Street, Area, City — full address"></textarea>
                          {errors.address && <small className="text-danger">{errors.address}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-12">
                          <button
                            className="btn btn-danger btn-block w-100 submit-btn rounded-1"
                            type="submit"
                            disabled={submitting}
                          >
                            {submitting
                              ? <><span className="listing-spinner"></span> Registering…</>
                              : <>Register My Gym Free <i className="bi bi-check-circle ms-2"></i></>
                            }
                          </button>
                          <div className="listing-form-trust">
                            <span><i className="bi bi-shield-fill-check text-success"></i> Secure</span>
                            <span><i className="bi bi-gift-fill text-danger"></i> Free Forever</span>
                            <span><i className="bi bi-clock-fill text-warning"></i> Live in 24h</span>
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats / Trust Bar ── */}
      <div className="listing-stats-bar" aria-label="Free listing highlights">
        <div className="container">
          <div className="listing-stats-grid">
            <div className="listing-stats-item">
              <i className="bi bi-buildings-fill"></i>
              <strong>5,000+</strong>
              <span>Gyms Listed</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-gift-fill"></i>
              <strong>₹0</strong>
              <span>Listing Cost</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-lightning-charge-fill"></i>
              <strong>24 hrs</strong>
              <span>Go Live Time</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── How It Works ── */}
      <section className="listing-how-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Simple Process</span>
            <h2>How to List Your Gym — 3 Easy Steps</h2>
            <p>Get your gym visible to thousands of fitness seekers in your city within 24 hours.</p>
          </div>
          <div className="listing-how-grid">
            <div className="listing-how-card">
              <div className="listing-how-num">01</div>
              <div className="listing-how-visual"><i className="bi bi-ui-checks-grid"></i><span>Gym details</span></div>
              <h3>Fill the Form</h3>
              <p>Enter your gym name, owner details, city, address, and contact information in the registration form above.</p>
            </div>
            <div className="listing-how-arrow"><i className="bi bi-arrow-right"></i></div>
            <div className="listing-how-card">
              <div className="listing-how-num">02</div>
              <div className="listing-how-visual"><i className="bi bi-patch-check"></i><span>Team review</span></div>
              <h3>We Review It</h3>
              <p>Our team checks the submitted information to help keep MeraGym listings useful and trustworthy.</p>
            </div>
            <div className="listing-how-arrow"><i className="bi bi-arrow-right"></i></div>
            <div className="listing-how-card">
              <div className="listing-how-num">03</div>
              <div className="listing-how-visual"><i className="bi bi-rocket-takeoff"></i><span>Live profile</span></div>
              <h3>Start Getting Discovered</h3>
              <p>Once live, fitness seekers can find your profile and contact you about memberships in their area.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits for Gym Owners ── */}
      <section className="listing-benefits-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Why Choose Us</span>
            <h2>Everything a Gym Owner Needs to Grow Online</h2>
            <p>MeraGym gives gym owners the digital tools to attract members and manage their presence — all for free.</p>
          </div>
          <div className="listing-benefits-grid">
            {[
              {
                image: listingBenefitsDiscoveryImage,
                alt: "Fitness seeker arriving at a gym",
                eyebrow: "Be easy to find",
                title: "Turn local searches into real conversations",
                benefits: [
                  { icon: "bi-search", title: "Get Discovered", desc: "Appear in search results when people look for gyms in your city or area." },
                  { icon: "bi-envelope-check-fill", title: "Free Leads & Enquiries", desc: "Receive real membership enquiries directly from interested fitness seekers." },
                ],
              },
              {
                image: listingBenefitsTrustImage,
                alt: "Gym coach speaking with a member",
                eyebrow: "Earn their confidence",
                title: "Give people a reason to choose your gym",
                benefits: [
                  { icon: "bi-star-fill", title: "Build Your Reputation", desc: "Collect reviews and ratings to build trust and stand out from competitors." },
                  { icon: "bi-shield-check", title: "Verified Badge", desc: "Get a verified gym badge after review — builds instant credibility with users." },
                ],
              },
              {
                image: listingBenefitsOperationsImage,
                alt: "Gym manager reviewing daily operations",
                eyebrow: "Run the details well",
                title: "Manage members and promote your best offers",
                benefits: [
                  { icon: "bi-cpu-fill", title: "Gym Software Access", desc: "Get access to our basic gym management software to handle members and billing." },
                  { icon: "bi-megaphone-fill", title: "Special Offers Platform", desc: "Promote membership deals and discounts to attract new members faster." },
                ],
              },
            ].map((group) => (
              <article className="listing-benefit-card listing-benefit-card--visual" key={group.title}>
                <div className="listing-benefit-media">
                  <img src={group.image} alt={group.alt} loading="lazy" />
                </div>
                <div className="listing-benefit-content">
                  <span className="listing-benefit-eyebrow">{group.eyebrow}</span>
                  <h3>{group.title}</h3>
                  <div className="listing-benefit-list">
                    {group.benefits.map((benefit) => (
                      <div className="listing-benefit-item" key={benefit.title}>
                        <div className="listing-benefit-icon"><i className={`bi ${benefit.icon}`}></i></div>
                        <div>
                          <h4>{benefit.title}</h4>
                          <p>{benefit.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Listing product showcase ── */}
      <section className="listing-showcase-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Built for gym owners</span>
            <h2>Your Listing Does More Than Put You on the Map</h2>
            <p>Use MeraGym to be easier to discover and to run the day-to-day parts of your gym with more confidence.</p>
          </div>
          <div className="listing-showcase-grid">
            <article className="listing-showcase-card">
              <div className="listing-showcase-copy">
                <span className="listing-showcase-kicker"><i className="bi bi-grid-1x2-fill"></i> Gym software</span>
                <h3>Keep your operations together</h3>
                <p>Move beyond spreadsheets with a simple toolkit designed around everyday gym work.</p>
              </div>
              <div className="listing-showcase-media listing-showcase-media--software">
                <img src={listingManagementImage} alt="Gym owner managing their fitness center" loading="lazy" />
              </div>
              <ul>
                <li>Member and attendance management</li>
                <li>Billing and payment tracking</li>
                <li>Reports and support when you need it</li>
              </ul>
            </article>
            <article className="listing-showcase-card listing-showcase-card--reverse">
              <div className="listing-showcase-copy">
                <span className="listing-showcase-kicker"><i className="bi bi-search"></i> Discoverability</span>
                <h3>Give future members a clear reason to choose you</h3>
                <p>Present your gym where people are already searching for a new place to train.</p>
              </div>
              <div className="listing-showcase-media listing-showcase-media--discovery">
                <img src={listingDiscoveryImage} alt="Gym owner welcoming a prospective member" loading="lazy" />
              </div>
              <ul>
                <li>Appear in city and category searches</li>
                <li>Show your facilities, contact details, and offers</li>
                <li>Build trust through a reviewed profile</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="listing-testimonials-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Success Stories</span>
            <h2>What Gym Owners Say About MeraGym</h2>
            <p>Real experiences from gym owners who grew their business by listing on MeraGym.</p>
          </div>
          <div className="listing-testimonials-grid">
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"Since listing on MeraGym, we get 15–20 genuine membership enquiries every month. The profile setup was super easy and completely free. Best decision for our gym's online presence."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">R</div>
                <div>
                  <strong>Rahul Sharma</strong>
                  <span>Owner, PowerFit Gym — Varanasi</span>
                </div>
              </div>
            </div>
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"MeraGym helped us double our walk-ins within 3 months. The verified badge gave us instant credibility. I recommend every gym owner to list here — it's truly free and worth it."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">P</div>
                <div>
                  <strong>Priya Nair</strong>
                  <span>Owner, ZenFit Studio — Noida</span>
                </div>
              </div>
            </div>
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"The gym software access that came with the free listing is a bonus. We now manage our entire member billing and attendance through MeraGym. Highly recommended for gym owners."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">A</div>
                <div>
                  <strong>Amit Verma</strong>
                  <span>Owner, IronCore Fitness — Lucknow</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Faq page="free_gym_listing" />

      {/* ── App Download ── */}
      <div className="app-dwnlod-area">
        <div className="dwnlod-p4">
          <h4>Download our fitness app and redefine your journey to greatness!</h4>
          <h6 className="mobl-h6">Download our fitness app</h6>
          <div className="dwnlod-p4-item">
            <a href="https://apps.apple.com" target="_blank" rel="noreferrer">
              <img src={appstore} alt="Download MeraGym on App Store" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.techup.mygym" target="_blank" rel="noreferrer">
              <img src={playstore} alt="Download MeraGym on Google Play" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
